import { Button } from "primereact/button";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import PrimeTable from "../../../components/PrimeTable";
import { useCallback, useEffect, useMemo, useState } from "react";
import appActions from '../../../appActions';
import StoreItemFilterForm from "./StoreItemFilterForm";
import { ConfirmationDialog } from "../../../components/ConfirmationDialog";

const StoreItemsList = () => {

    let navigate = useNavigate();
    const dispatch = useDispatch();

    const [deleteRecord, setDeleteRecord] = useState(null); // Store record for deletion
    
    const storeItemList = useSelector((state) => state.storeItems.itemList);

    const storeitem = useSelector((state) => state.storeItems.item);
    
    const filters = useSelector((state) => state.storeItems.filters);

    const defaultParams = useMemo(() => ({
        ...filters,
        pageno: 1,
        pagesize: 50,
        sort: 'createdOn',
        sortOrder: -1,
    }), [filters]);

    const [formValues, setFormValues] = useState(defaultParams);
    

    const handleFormValuesChange = (values) => {
        setFormValues(values);
    };

    const handleReset = () => {
        setFormValues(defaultParams)
        dispatch(appActions.STOREITEMS_GET_ALL_REQUEST(defaultParams));
    }

    const handleRefresh = useCallback(() => {
        dispatch(appActions.STOREITEMS_GET_ALL_REQUEST(defaultParams));
    }, [dispatch, defaultParams]);

    useEffect(() => {
        if (storeitem?.action === 'delete') {
            handleRefresh();
        }
    }, [storeitem, handleRefresh]);
    
    const handleSearch = useCallback(
        (params) => {
            const searchParams = {
                ...params,
                pagesize: params.pagesize || defaultParams?.pagesize,
                pageno: params.pageno || defaultParams?.pageno,
                sort: params.sort || defaultParams?.sort,
                sortOrder: params.sortOrder || defaultParams?.sortOrder,
            };
            dispatch(appActions.STOREITEMS_GET_ALL_REQUEST(searchParams));
        },
        [dispatch, defaultParams?.pageno, defaultParams?.pagesize, defaultParams?.sort, defaultParams?.sortOrder], // Add missing dependencies
    );

    useEffect(() => {
        handleSearch(defaultParams);
    }, []); // Empty dependency array ensures it runs only on mount
    
    const linkToNew = useCallback(
        () => navigate('/ui/store-item/details', { replace: false }),
        [navigate],
    );

    const handleViewRedirect = useCallback((record) => {
        const data = storeItemList?.data?.find(t => t.id === record.id)
        navigate('/ui/store-item/details', { state: { data, isView: true } });
    }, [storeItemList, navigate]);
  
    const handleEditRedirect = useCallback((record) => {
        const data = storeItemList?.data?.find(t => t.id === record.id)
        navigate('/ui/store-item/details', { state: { data, isEdit: true } });
    }, [storeItemList, navigate]);

   
    const handleDelete = useCallback((record) => {
        setDeleteRecord(record); 
    }, []);

    const handleDeleteConfirm = useCallback(() => {
        if (deleteRecord) {
            dispatch(appActions.STOREITEMS_DELETE_REQUEST({ id: deleteRecord.id }));
            setDeleteRecord(null); // Clear the record after deletion
    
            // Explicitly refresh the table after deletion
            setTimeout(() => {
                handleRefresh();
            }, 500); // Add a slight delay if needed to allow the backend to process
        }
    }, [dispatch, deleteRecord, handleRefresh])

    const renderHeader = () => {
        return (
            <div className="d-flex flex-row gap-10 align-items-center">
                <h1 className="page-header flex"> Store Items </h1>
                <Button icon="pi pi-sync" rounded text aria-label="Refresh" className='mr-auto tp' data-pr-tooltip="Refresh" onClick={handleRefresh} />
                <Button label="Add New" icon="pi pi-plus" className="p-button-outlined tp" data-pr-tooltip="Add New" onClick={linkToNew} />
            </div>
        );
    };

    const actionTemplate = useCallback(
        (rowData) => (
            <div className="flex gap-10 justify-content-center">
                <span className="cursor-pointer tp" onClick={() => handleViewRedirect(rowData)} data-pr-tooltip="View">
                    <i className="pi pi-eye text-blue-500 text-base"></i>
                </span>
                <span className="cursor-pointer tp" onClick={() => handleEditRedirect(rowData)} data-pr-tooltip="Edit">
                    <i className="pi pi-pencil text-blue-500 text-base"></i>
                </span>
                <span className="cursor-pointer tp" onClick={() => handleDelete(rowData)} data-pr-tooltip="Delete">
                    <i className="pi pi-times-circle text-base text-danger"/>
                </span>
            </div>
        ),
        [handleViewRedirect, handleEditRedirect, handleDelete],
    );

    const onFilterChangeCallback  = useCallback(
        (e) => {

            const { sortField, sortOrder, rows, page } = e;
            
            const params = {...formValues, pagesize: rows, pageno: page, sort: sortField, sortOrder: sortOrder };

            setFormValues(prevFormValues => ({
                ...prevFormValues,
                sort: sortField, 
                sortOrder: sortOrder,
                pagesize: rows,
                pageno: page
            }));

            handleSearch(params); // Call search with updated sorting params
        },
        [handleSearch, formValues],
    );
 
    const columns = [
        { field: 'inventoryId', header: 'Inventory Id', sortable: true, fileLog: true },
        { field: 'title', header: 'Title', sortable: true, fileLog: true},
        { field: 'itemCode', header: 'Item Code', sortable: true },
        { field: 'barcodeType', header: 'Barcode Type', sortable: true},
        { field: 'barcode', header: 'Barcode', sortable: true},
        { field: 'pcModifierName', header: 'PC Modifier Name', sortable: true},
        { field: 'pcModifierValue', header: 'PC Modifier Value', sortable: true },
        { field: 'itemType', header: 'Item Type', sortable: true, fileLog: true, width: '110px'},
        { field: 'createdOn', header: 'Created On', sortable: true,  width: '140px', columnType : 'datetime'  },
        // { field: 'lastUpdated', header: 'Last Updated', sortable: true, columnType : 'datetime' },
        // { field: 'lastUpdatedBy', header: 'Last Updated By', sortable: true },
    ];

    const renderList = () => {
        return (
            <>
                <div className='pv-15 ph-15'>
                    {renderHeader()}
                </div>
                <div className="bg-white mx-3 pv-15 ph-15 br-6 shadow-white">
                    <StoreItemFilterForm 
                        handleSearch={handleSearch} 
                        handleReset={handleReset}  
                        onFormValuesChange={handleFormValuesChange} 
                    />
                    <div className='custom-devider'></div>
                    <PrimeTable
                        list={storeItemList}
                        columns={columns}
                        actionColumn={actionTemplate}
                        onFilterChangeCallback={(e) => {
                            onFilterChangeCallback(e);
                        }}
                        tableName='store_items'
                        isLazy={true}
                        showGenerateXmlBtn={false}
                    />
                </div>
            </>
        );
    };

    return <div className="form_height">
        {renderList()}
        {deleteRecord && (
            <ConfirmationDialog
                message={`Are you sure you want to delete the Store Item : ${deleteRecord.inventoryId}?`}
                onAccept={handleDeleteConfirm}
                onReject={() => setDeleteRecord(null)}
            />
        )}
    </div>;

}

export default StoreItemsList;
